import React, { useEffect, useRef } from 'react';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import SimpleSelect from './SimpleSelect';

export const TIKTOK_PRIVACY_LABELS = {
  PUBLIC_TO_EVERYONE: 'Öffentlich (alle)',
  MUTUAL_FOLLOW_FRIENDS: 'Freunde (gegenseitige Follower)',
  FOLLOWER_OF_CREATOR: 'Nur Follower',
  SELF_ONLY: 'Nur ich',
};
const ALL_PRIVACY = Object.keys(TIKTOK_PRIVACY_LABELS);

export const TIKTOK_PHOTO_TITLE_MAX = 90;
export const TIKTOK_PHOTO_DESC_MAX = 4000;
export const TIKTOK_VIDEO_CAPTION_MAX = 2200;
export const TIKTOK_MAX_PHOTOS = 32;

export const EMPTY_TIKTOK_OPTIONS = {
  privacyLevel: '',
  allowComment: true,
  allowDuet: true,
  allowStitch: true,
  brandContentToggle: false,
  brandOrganicToggle: false,
  isAigc: false,
  autoAddMusic: true,
  draft: false,
  videoCoverTimestampMs: 0,
  photoCoverIndex: 0,
  title: '',
  description: '',
};

/** UI state -> options object the backend expects. */
export function toTikTokPayload(opts, kind) {
  const out = {
    disableComment: !opts.allowComment,
    brandContentToggle: !!opts.brandContentToggle,
    brandOrganicToggle: !!opts.brandOrganicToggle,
    isAigc: !!opts.isAigc,
    draft: !!opts.draft,
  };
  if (opts.privacyLevel) out.privacyLevel = opts.privacyLevel;
  if (kind === 'video') {
    out.disableDuet = !opts.allowDuet;
    out.disableStitch = !opts.allowStitch;
    out.videoCoverTimestampMs = Math.max(0, Math.round(opts.videoCoverTimestampMs || 0));
  } else if (kind === 'photo') {
    out.autoAddMusic = !!opts.autoAddMusic;
    out.photoCoverIndex = opts.photoCoverIndex || 0;
  }
  return out;
}

/** Returns a German error message or null. Mirrors the backend rules. */
export function validateTikTok({ opts, kind, text, imageCount, durationSec, info }) {
  if (kind === 'none') return 'TikTok braucht ein Video oder mindestens ein Bild.';
  if (!opts.draft && !opts.privacyLevel) return 'TikTok: Bitte wähle aus, wer den Beitrag sehen darf.';
  if (opts.brandContentToggle && opts.privacyLevel === 'SELF_ONLY') {
    return 'TikTok: Bezahlte Partnerschaften können nicht „Nur ich“ sein.';
  }
  if (kind === 'photo') {
    const title = (text || '').trim().split('\n')[0];
    if (title.length > TIKTOK_PHOTO_TITLE_MAX) return `TikTok-Titel: maximal ${TIKTOK_PHOTO_TITLE_MAX} Zeichen.`;
    if ((text || '').length > TIKTOK_PHOTO_DESC_MAX) return `TikTok-Beschreibung: maximal ${TIKTOK_PHOTO_DESC_MAX} Zeichen.`;
    if (imageCount > TIKTOK_MAX_PHOTOS) return `TikTok erlaubt maximal ${TIKTOK_MAX_PHOTOS} Bilder.`;
  }
  if (kind === 'video') {
    if ((text || '').length > TIKTOK_VIDEO_CAPTION_MAX) return `TikTok-Beschreibung: maximal ${TIKTOK_VIDEO_CAPTION_MAX} Zeichen.`;
    const max = info?.maxVideoPostDurationSec;
    if (max && durationSec && durationSec > max) {
      return `Das Video ist ${Math.round(durationSec)} s lang – dieser TikTok-Account erlaubt maximal ${max} s.`;
    }
  }
  return null;
}

function ToggleRow({ id, label, hint, checked, onChange, disabled }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <Label htmlFor={id} className={`text-xs ${disabled ? 'text-[#5A6C7D]' : 'text-[#A0AEC0]'}`}>{label}</Label>
        {hint && <p className="text-[10px] text-[#5A6C7D] mt-0.5">{hint}</p>}
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={id} />
    </div>
  );
}

export default function TikTokOptionsPanel({
  value, onChange, kind, videoUrl, imageUrls = [], text = '',
  info, infoError, durationSec, onDuration,
}) {
  const set = (patch) => onChange({ ...value, ...patch });
  const videoRef = useRef(null);
  const privacyOptions = (info?.privacyLevelOptions?.length ? info.privacyLevelOptions : ALL_PRIVACY)
    .filter((o) => TIKTOK_PRIVACY_LABELS[o]);

  // Drop a privacy choice that TikTok does not offer this creator.
  useEffect(() => {
    if (value.privacyLevel && !privacyOptions.includes(value.privacyLevel)) set({ privacyLevel: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  // Respect account-level locks (creator disabled comments/duet/stitch in TikTok).
  useEffect(() => {
    if (!info) return;
    const patch = {};
    if (info.commentDisabled && value.allowComment) patch.allowComment = false;
    if (info.duetDisabled && value.allowDuet) patch.allowDuet = false;
    if (info.stitchDisabled && value.allowStitch) patch.allowStitch = false;
    if (Object.keys(patch).length) set(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  const maxDur = info?.maxVideoPostDurationSec;
  const tooLong = kind === 'video' && maxDur && durationSec && durationSec > maxDur;
  const privateBranded = value.brandContentToggle && value.privacyLevel === 'SELF_ONLY';
  const photoTitle = (text || '').trim().split('\n')[0];

  return (
    <div className="border border-[#1A2B42] rounded p-3 space-y-3 bg-[#0A1628]/60" data-testid="tiktok-options-panel">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-white">TikTok-Einstellungen</span>
        {info?.nickname && <span className="text-[10px] text-[#5A6C7D]">@{info.nickname}</span>}
      </div>
      {infoError && (
        <p className="text-[10px] text-amber-300/90" data-testid="tiktok-info-error">
          {infoError} Es werden die Standardoptionen angezeigt; TikTok prüft beim Veröffentlichen erneut.
        </p>
      )}

      {/* Privacy */}
      <div>
        <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Wer darf den Beitrag sehen? {!value.draft && '*'}</Label>
        <SimpleSelect
          value={value.privacyLevel}
          onChange={(v) => set({ privacyLevel: v })}
          options={privacyOptions.map((o) => ({ value: o, label: TIKTOK_PRIVACY_LABELS[o] }))}
          placeholder="Bitte auswählen…"
          testId="tiktok-privacy-select"
        />
        {privateBranded && (
          <p className="text-[10px] text-red-400 mt-1">Bezahlte Partnerschaften können nicht „Nur ich“ sein.</p>
        )}
      </div>

      {/* Interactions */}
      <div className="divide-y divide-[#1A2B42]">
        <ToggleRow id="tiktok-allow-comment" label="Kommentare erlauben" checked={value.allowComment}
          onChange={(v) => set({ allowComment: v })} disabled={!!info?.commentDisabled}
          hint={info?.commentDisabled ? 'In deinem TikTok-Account deaktiviert.' : undefined} />
        {kind === 'video' && (
          <>
            <ToggleRow id="tiktok-allow-duet" label="Duett erlauben" checked={value.allowDuet}
              onChange={(v) => set({ allowDuet: v })} disabled={!!info?.duetDisabled}
              hint={info?.duetDisabled ? 'In deinem TikTok-Account deaktiviert.' : undefined} />
            <ToggleRow id="tiktok-allow-stitch" label="Stitch erlauben" checked={value.allowStitch}
              onChange={(v) => set({ allowStitch: v })} disabled={!!info?.stitchDisabled}
              hint={info?.stitchDisabled ? 'In deinem TikTok-Account deaktiviert.' : undefined} />
          </>
        )}
      </div>

      {/* Video: cover frame + duration */}
      {kind === 'video' && videoUrl && (
        <div>
          <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Thumbnail (Cover-Frame)</Label>
          <video
            ref={videoRef}
            src={videoUrl}
            muted
            playsInline
            preload="metadata"
            className="w-full max-h-40 object-contain bg-black rounded"
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              if (Number.isFinite(d)) onDuration?.(d);
              e.currentTarget.currentTime = (value.videoCoverTimestampMs || 0) / 1000;
            }}
            data-testid="tiktok-cover-video"
          />
          {durationSec > 0 && (
            <>
              <input
                type="range"
                min={0}
                max={Math.max(0, Math.floor(durationSec * 1000))}
                step={100}
                value={Math.min(value.videoCoverTimestampMs || 0, Math.floor(durationSec * 1000))}
                onChange={(e) => {
                  const ms = Number(e.target.value);
                  set({ videoCoverTimestampMs: ms });
                  if (videoRef.current) videoRef.current.currentTime = ms / 1000;
                }}
                className="w-full mt-2 accent-[#00D9C8]"
                data-testid="tiktok-cover-slider"
              />
              <div className="flex justify-between text-[10px] text-[#5A6C7D]">
                <span>Cover bei {((value.videoCoverTimestampMs || 0) / 1000).toFixed(1)} s</span>
                <span>Länge {durationSec.toFixed(1)} s{maxDur ? ` · max. ${maxDur} s` : ''}</span>
              </div>
            </>
          )}
          {tooLong && (
            <p className="text-[10px] text-red-400 mt-1" data-testid="tiktok-too-long">
              Das Video ist länger als dein TikTok-Account erlaubt ({maxDur} s).
            </p>
          )}
        </div>
      )}

      {/* Photo carousel */}
      {kind === 'photo' && (
        <div className="space-y-3">
          <p className="text-[10px] text-[#5A6C7D]" data-testid="tiktok-photo-hint">
            Titel = erste Zeile des Textes{' '}
            <span className={photoTitle.length > TIKTOK_PHOTO_TITLE_MAX ? 'text-red-400' : ''}>({photoTitle.length}/{TIKTOK_PHOTO_TITLE_MAX})</span>
            , Beschreibung = gesamter Text ({(text || '').length}/{TIKTOK_PHOTO_DESC_MAX}).
          </p>
          {imageUrls.length > 1 && (
            <div>
              <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Cover-Bild</Label>
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {imageUrls.map((u, i) => (
                  <button
                    key={u + i}
                    type="button"
                    onClick={() => set({ photoCoverIndex: i })}
                    className={`relative w-12 h-12 flex-shrink-0 rounded overflow-hidden border ${value.photoCoverIndex === i ? 'border-[#00D9C8]' : 'border-[#1A2B42]'}`}
                    data-testid={`tiktok-cover-image-${i}`}
                  >
                    <img src={u} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#5A6C7D] mt-1">Alle Bilder brauchen dasselbe Seitenverhältnis.</p>
            </div>
          )}
          <ToggleRow id="tiktok-auto-music" label="Hintergrundmusik automatisch hinzufügen"
            checked={value.autoAddMusic} onChange={(v) => set({ autoAddMusic: v })} />
        </div>
      )}

      {/* Disclosure */}
      <div className="divide-y divide-[#1A2B42]">
        <ToggleRow id="tiktok-brand-content" label="Bezahlte Partnerschaft"
          hint="Du wirst für den Beitrag bezahlt oder erhältst Produkte (Branded Content)."
          checked={value.brandContentToggle} onChange={(v) => set({ brandContentToggle: v })} />
        <ToggleRow id="tiktok-brand-organic" label="Eigene Marke bewerben"
          hint="Du bewirbst dein eigenes Unternehmen oder Produkt."
          checked={value.brandOrganicToggle} onChange={(v) => set({ brandOrganicToggle: v })} />
        <ToggleRow id="tiktok-aigc" label="KI-generierter Inhalt"
          hint="Kennzeichnet den Beitrag als mit KI erstellt."
          checked={value.isAigc} onChange={(v) => set({ isAigc: v })} />
        <ToggleRow id="tiktok-draft" label="Als Entwurf an TikTok senden"
          hint="Landet in deiner TikTok-Inbox und wird dort fertiggestellt, statt automatisch zu veröffentlichen."
          checked={value.draft} onChange={(v) => set({ draft: v })} />
      </div>
    </div>
  );
}
